import { ScheduleConstraintType } from '@e2m-customer-portal/domains';
import { Nullable } from '@e2m-customer-portal/shared';
import {
  InfoTooltip,
  NumberInput,
  TimeInput,
  ValidatedContainer,
  WeekdaySelection,
  WeekdaySelector,
} from '@e2m-customer-portal/ui';
import Button from '@mui/material/Button';
import clsx from 'clsx';
import { Dispatch, SetStateAction, memo, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IMask } from 'react-imask';

export const quarterMaskOptions = {
  mask: 'HH:mm',
  overwrite: true,
  blocks: {
    HH: {
      mask: IMask.MaskedRange,
      from: 0,
      to: 23,
    },
    mm: {
      mask: IMask.MaskedEnum,
      enum: ['00', '15', '30', '45'],
      autofix: 'true',
    },
  },
};

export interface ScheduleConstProps {
  data: ScheduleConstraintType;
  scheduleConstraints: ScheduleConstraintType[];
  setScheduleConstrains: Dispatch<SetStateAction<ScheduleConstraintType[] | undefined>>;
  setSaveBarOpen: Dispatch<SetStateAction<boolean>>;
  readOnly: (parameter?: string) => boolean;
  setIsValid: Dispatch<SetStateAction<boolean>>;
}

function ScheduleConstraintMemo({
  data,
  scheduleConstraints,
  setScheduleConstrains,
  setSaveBarOpen,
  readOnly,
  setIsValid,
}: ScheduleConstProps) {
  const [t] = useTranslation();

  const [updatedConstraint, setUpdatedConstraint] = useState<ScheduleConstraintType>(data);

  useEffect(() => {
    const schedule_constraints = scheduleConstraints.map((item) =>
      item.identifier === updatedConstraint.identifier ? updatedConstraint : item,
    );
    schedule_constraints && setScheduleConstrains(schedule_constraints);
  }, [updatedConstraint]);

  useEffect(() => {
    setUpdatedConstraint(data);
  }, [data]);

  return (
    <div className="relative px-4 py-6 grid grid-cols-1 xl:grid-cols-2 gap-x-2 gap-y-4 bg-grey-50 rounded-md border-solid border border-divider">
      <ValidatedContainer validate="on" onValidated={setIsValid}>
        <Button
          color="error"
          className="absolute -right-4 -top-4 bg-white h-8 w-8 min-w-8 shrink-0 grow-0 items-center justify-center rounded-full hover:bg-error-500 hover:text-white z-10"
          variant="outlined"
          disabled={readOnly('schedule_constraints')}
          onClick={() => {
            setSaveBarOpen(true);
            setScheduleConstrains([
              ...scheduleConstraints.filter((item) => item.identifier !== updatedConstraint.identifier),
            ]);
          }}
        >
          <span className="-translate-y-0.5">x</span>
        </Button>
        {updatedConstraint.start_time !== undefined && (
          <TimeInput
            maskOptions={quarterMaskOptions}
            value={updatedConstraint.start_time}
            name="start_time"
            className="p-2 bg-white"
            label={t('COPT_MOTORS_SCHEDULE_CONSTRAINTS_START_TIME')}
            disabled={readOnly('schedule_constraints')}
            onChange={(e, value) => {
              setSaveBarOpen(true);
              setUpdatedConstraint({ ...updatedConstraint, start_time: value });
            }}
          />
        )}
        {updatedConstraint.end_time !== undefined && (
          <TimeInput
            maskOptions={quarterMaskOptions}
            value={updatedConstraint.end_time}
            name="end_time"
            className="p-2 bg-white"
            label={t('COPT_MOTORS_SCHEDULE_CONSTRAINTS_END_TIME')}
            disabled={readOnly('schedule_constraints')}
            onChange={(e, value) => {
              setSaveBarOpen(true);
              setUpdatedConstraint({ ...updatedConstraint, end_time: value });
            }}
          />
        )}
        {updatedConstraint.minimum_power !== undefined && (
          <NumberInput
            value={updatedConstraint.minimum_power === null ? null : updatedConstraint.minimum_power * 1000}
            className="w-full bg-white"
            name="minimum_power"
            label={t('COPT_MOTORS_SCHEDULE_CONSTRAINTS_MINIMUM_POWER')}
            onChange={(value: Nullable<number>) => {
              setSaveBarOpen(true);
              setUpdatedConstraint({
                ...updatedConstraint,
                minimum_power: value != null && !Number.isNaN(value) ? value / 1000 : null,
              });
            }}
            disabled={readOnly('schedule_constraints')}
            notNegative
            endAdornment={<InfoTooltip field="minimum_power" objectType="motors_schedule_constraints" />}
          />
        )}
        {updatedConstraint.maximum_power !== undefined && (
          <NumberInput
            value={updatedConstraint.maximum_power === null ? null : updatedConstraint.maximum_power * 1000}
            className="w-full bg-white"
            name="maximum_power"
            label={t('COPT_MOTORS_SCHEDULE_CONSTRAINTS_MAXIMUM_POWER')}
            onChange={(value: Nullable<number>) => {
              setSaveBarOpen(true);
              setUpdatedConstraint({
                ...updatedConstraint,
                maximum_power: value != null && !Number.isNaN(value) ? value / 1000 : null,
              });
            }}
            disabled={readOnly('schedule_constraints')}
            notNegative
            endAdornment={<InfoTooltip field="maximum_power" objectType="motors_schedule_constraints" />}
          />
        )}
        <WeekdaySelector
          pythonMode
          selected={updatedConstraint.days_of_week?.reduce((a, v) => ({ ...a, [v]: true }), {}) as WeekdaySelection}
          disabled={readOnly('schedule_constraints')}
          className={clsx('p-4 rounded-md bg-white border-solid border border-divider xl:col-span-2', {
            'opacity-70': readOnly('schedule_constraints'),
          })}
          onChange={(selected) => {
            setSaveBarOpen(true);
            setUpdatedConstraint({
              ...updatedConstraint,
              days_of_week: Object.entries(selected)
                .filter(([, isSelected]) => isSelected)
                .map(([day]) => +day),
            });
          }}
        />
      </ValidatedContainer>
    </div>
  );
}
export const ScheduleConstraint = memo(ScheduleConstraintMemo);
